import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import Placeholder from 'components/consumer/Placeholder';
import FullScreenModal from 'components/consumer/FullScreenModal';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import JBImage from 'components/essentials/JBImage';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import PDPProductDetailsLinkSection from './PDPProductDetailsLinkSection';

const PDPProductDetailsModalUI = ({
  isOpen,
  onClose,
  loading,
  productDetails,
  productCare,
  productImage,
  tearSheetLink,
  assemblyDetailsLink,
}) => {
  // This component is wrapped in withBrowserOnly so it is safe to use a media
  // query conditional here.
  const isLargeDesktop = useMediaQuery({
    query: '(min-width: 1280px)',
  });

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      enableHashHistory={false}
    >
      {loading ? (
        <div className={classNames('relative max-[1023px]:pt-4 lg:pt-20')}>
          <FixedWidthContainer noMobilePadding>
            <div className="w-full xl:flex">
              <div className="lg:pb-0 xl:w-[67%]">
                <Placeholder
                  loading
                  type="rect"
                  className="text-2xl font-bold text-gray mt-0 mx-0 mb-6 max-h-[30px] lg:max-[1279px]:mt-24"
                  style={{ marginBottom: 32, width: 349 }}
                />

                <div className="lg:[column-count:2] lg:[column-gap:9rem]">
                  {[0, 1, 2, 3, 4].map(idx => (
                    <div key={idx} className="break-inside-avoid mb-8">
                      <Placeholder
                        loading
                        type="rect"
                        className="text-base font-bold text-gray min-h-[24px]"
                        style={{ marginBottom: 10 }}
                      />

                      <Placeholder
                        loading
                        type="rect"
                        className="text-sm text-gray pb-0 min-h-[100px]"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="max-[1023px]:mt-16 xl:ml-36 xl:w-[33%] lg:max-[1279px]:pr-[calc(50%_+_4.5rem)]">
                <Placeholder
                  loading
                  type="rect"
                  className="text-2xl font-bold text-gray mt-0 mx-0 mb-6 max-h-[30px] lg:max-[1279px]:mt-24"
                  style={{ marginBottom: 32 }}
                />

                <ul className="ml-0 pl-4">
                  {[0, 1, 2, 3, 4].map(id => (
                    <Placeholder
                      key={id}
                      loading
                      type="rect"
                      className="text-sm text-gray mb-4 min-h-[30px] mr-[0x]"
                    />
                  ))}
                </ul>
              </div>
            </div>
          </FixedWidthContainer>
        </div>
      ) : (
        <div className="relative max-[1023px]:pt-4 lg:pt-20">
          <FixedWidthContainer noMobilePadding>
            <div className="w-full xl:flex">
              {!!productDetails?.length && (
                <div
                  className={classNames({
                    'xl:w-[67%]': !!productCare?.length,
                    'lg:pb-[500px]': !!productImage && !productCare?.length,
                    'lg:pb-0': !!productImage && !!productCare?.length,
                  })}
                >
                  <h3
                    className={classNames(
                      'text-2xl font-bold text-gray mt-0 mx-0 mb-6',
                      {
                        'lg:max-[1279px]:mt-24': !!productCare?.length,
                      }
                    )}
                  >
                    Details
                  </h3>

                  <div className="lg:[column-count:2] lg:[column-gap:9rem]">
                    {productDetails.map(({ label, description }) => (
                      <div key={label} className="break-inside-avoid mb-8">
                        <div className="text-base font-bold text-gray">
                          {label}
                        </div>

                        <div className="text-sm text-gray pb-0">
                          {description}
                        </div>
                      </div>
                    ))}
                  </div>

                  {(!productCare?.length || isLargeDesktop) && (
                    <div className="mt-12 mx-0 mb-8 flex flex-row gap-6 max-[1023px]:flex-col">
                      <PDPProductDetailsLinkSection link={tearSheetLink}>
                        Download Tear Sheet
                      </PDPProductDetailsLinkSection>

                      <PDPProductDetailsLinkSection link={assemblyDetailsLink}>
                        Download Assembly Details
                      </PDPProductDetailsLinkSection>
                    </div>
                  )}
                </div>
              )}

              {!!productCare?.length && (
                <>
                  <div
                    className={classNames(
                      'max-[1023px]:mt-16 xl:ml-36 xl:w-[33%] lg:max-[1279px]:pr-[calc(50%_+_4.5rem)]',
                      {
                        'lg:max-[1279px]:h-[500px] xl:pb-[500px]': !!productImage,
                      }
                    )}
                  >
                    <h3 className="text-2xl font-bold text-gray mt-0 mx-0 mb-6 lg:max-[1279px]:mt-24">
                      Product Care
                    </h3>

                    <ul className="ml-0 pl-4">
                      {productCare.map(productCareItem => (
                        <li
                          key={productCareItem}
                          className="text-sm text-gray mb-4"
                        >
                          {productCareItem}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {!!productCare?.length && !isLargeDesktop && (
                    <div className="mt-12 mx-0 mb-8 flex flex-row gap-6 max-[1023px]:flex-col">
                      <PDPProductDetailsLinkSection link={tearSheetLink}>
                        Download Tear Sheet
                      </PDPProductDetailsLinkSection>

                      <PDPProductDetailsLinkSection link={assemblyDetailsLink}>
                        Download Assembly Details
                      </PDPProductDetailsLinkSection>
                    </div>
                  )}
                </>
              )}
            </div>
          </FixedWidthContainer>

          {!!productImage && (
            <JBImage
              className="hidden absolute bottom-0 right-0 object-contain object-right-bottom lg:max-w-[calc(50vw_-_20px)] lg:block xl:max-w-[510px]"
              height="480px"
              src={productImage}
            />
          )}
        </div>
      )}
    </FullScreenModal>
  );
};

PDPProductDetailsModalUI.propTypes = {
  loading: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  productDetails: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  productCare: PropTypes.arrayOf(PropTypes.string),
  productImage: PropTypes.string,
  tearSheetLink: PropTypes.string,
  assemblyDetailsLink: PropTypes.string,
};

export default withBrowserOnly(PDPProductDetailsModalUI);
